import React, { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import { Box } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import AppsIcon from '@material-ui/icons/Apps';
import VideocamIcon from '@material-ui/icons/Videocam';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import GetAppIcon from '@material-ui/icons/GetApp';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import { CameraTile } from '@vgps/livekit_player';
import { showGlobalToast } from 'src/features/uiSlice';
import {
  DEFAULT_LIVE_QUALITY,
  getLiveQualityStorageKey,
  primeDefaultLiveQuality
} from 'src/app/sdk/livekit/qualityDefaults';
import { useAudioAffordance } from 'src/app/sdk/livekit/audioAffordance';
import { LOST_GSM } from 'src/app/utils/vehicleService';

// Build-time tunable: defer 'recovering' overlay. undefined → CameraTile default.
const RECOVERING_OVERLAY_DELAY_MS = (() => {
  const v = Number(process.env.REACT_APP_LIVEKIT_RECOVERING_OVERLAY_MS);
  return Number.isFinite(v) && v >= 0 ? v : undefined;
})();

const CAMERA_CHANNELS = ['1', '2', '3', '4'];
const LIVE_CAMERA_CHANNELS = new Set(['1', '2', '3', '4']);
const SNAPSHOT_CHANNEL_SUFFIXES = new Set(['01', '02']);
const WASM_MEMORY_REFRESH_EVENT = 'vgps-wasm-memory-refresh';

export const hasCameraDevice = vehicle => {
  const deviceType =
    vehicle?.device_type || vehicle?.vehicle?.device_type || vehicle?.vehicle_type;
  return ['VGPS_4CAM', 'VGPS_2CAM'].includes(deviceType);
};

const getMaxCameraCount = deviceType => (deviceType === 'VGPS_2CAM' ? 2 : 4);

const getCameraViewOptions = deviceType => {
  const maxCameraCount = getMaxCameraCount(deviceType);
  return [
    { label: '1x1', count: 1 },
    { label: '1x2', count: 2 },
    ...(maxCameraCount === 4 ? [{ label: '1x4', count: 4 }] : [])
  ];
};

const CameraViewOptionIcon = ({ count }) => (
  <Box
    className={`tracking-camera-layout-icon tracking-camera-layout-icon-${count}`}
    aria-hidden="true"
  >
    {Array.from({ length: count }).map((_, index) => (
      <Box className="tracking-camera-layout-bar" key={`${count}-${index}`} />
    ))}
  </Box>
);

const CameraPlaceholder = () => (
  <Box className="tracking-camera-tile-placeholder">
    <VideocamIcon className="tracking-camera-empty-icon" />
  </Box>
);

const getBaseChannelFromImageCamera = camera => {
  const normalized = String(camera || '');
  if (normalized.length < 3) {
    return normalized;
  }

  const suffix = normalized.slice(-2);
  return SNAPSHOT_CHANNEL_SUFFIXES.has(suffix)
    ? normalized.slice(0, -2)
    : normalized;
};

const TrackingCameraToolbar = ({
  isVisible,
  isMuted,
  // Speaker-button presentation from the three-state capability contract.
  // Defaulted so the snapshot/image toolbars, which pass no audio state, keep
  // their existing behaviour untouched.
  audio = { disabled: false, label: null },
  isFullscreen,
  showMuteButton = true,
  actionIcon = <PhotoCameraIcon />,
  actionLabel = 'Chụp ảnh camera',
  actionTitle = 'Chụp ảnh',
  onToggleMute,
  onScreenshot,
  onToggleFullscreen
}) => (
  <Box
    className={`tracking-camera-toolbar ${isVisible ? 'tracking-camera-toolbar-visible' : ''
      }`}
  >
    <Box className="tracking-camera-toolbar-group">
      {showMuteButton && (
        <Box
          component="button"
          type="button"
          className={`tracking-camera-tool-button ${audio.disabled ? 'tracking-camera-tool-button-disabled' : ''}`}
          onClick={audio.disabled ? undefined : onToggleMute}
          // aria-disabled without the `disabled` attribute: `disabled` drops the
          // button out of the tab order, so a keyboard user can never reach the
          // reason it is unavailable — which is the whole point of dimming it
          // rather than hiding it. It is already inert (onClick is undefined).
          aria-disabled={audio.disabled || undefined}
          aria-label={audio.label || (isMuted ? 'Bật âm thanh' : 'Tắt âm thanh')}
          title={audio.label || (isMuted ? 'Bật âm thanh' : 'Tắt âm thanh')}
        >
          {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
        </Box>
      )}
    </Box>
    <Box className="tracking-camera-toolbar-group">
      <Box
        component="button"
        type="button"
        className="tracking-camera-tool-button"
        onClick={onScreenshot}
        aria-label={actionLabel}
        title={actionTitle}
      >
        {actionIcon}
      </Box>
      <Box
        component="button"
        type="button"
        className="tracking-camera-tool-button"
        onClick={onToggleFullscreen}
        aria-label={isFullscreen ? 'Thoát toàn màn hình camera' : 'Mở toàn màn hình camera'}
        title={isFullscreen ? 'Thoát toàn màn hình' : 'Toàn màn hình'}
      >
        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </Box>
    </Box>
  </Box>
);

const TrackingCameraImageTile = ({ image, channel }) => {
  const containerRef = useRef(null);
  const fullscreenCleanupTimerRef = useRef(null);
  const wasFullscreenRef = useRef(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isFullscreenHovered, setIsFullscreenHovered] = useState(false);
  const fsTimeoutRef = useRef(null);

  useEffect(() => {
    const syncFullscreenState = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };

    document.addEventListener('fullscreenchange', syncFullscreenState);
    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreenState);
    };
  }, []);

  useEffect(() => {
    if (!isFullscreen) {
      setIsFullscreenHovered(false);
      if (fsTimeoutRef.current) {
        clearTimeout(fsTimeoutRef.current);
        fsTimeoutRef.current = null;
      }
      return;
    }

    const handleMouseMove = () => {
      setIsFullscreenHovered(true);
      if (fsTimeoutRef.current) {
        clearTimeout(fsTimeoutRef.current);
      }
      fsTimeoutRef.current = setTimeout(() => {
        setIsFullscreenHovered(false);
      }, 2500);
    };

    const handleMouseLeave = () => {
      setIsFullscreenHovered(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);
    handleMouseMove();

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      if (fsTimeoutRef.current) {
        clearTimeout(fsTimeoutRef.current);
      }
    };
  }, [isFullscreen]);

  useEffect(() => () => {
    if (fsTimeoutRef.current) {
      clearTimeout(fsTimeoutRef.current);
    }
  }, []);

  const handleFullscreenChange = useCallback(isFs => {
    const tileEl = containerRef.current;
    if (!tileEl) return;
    if (isFs) {
      wasFullscreenRef.current = true;
      tileEl.classList.remove('tracking-camera-tile-returning');
      tileEl
        .closest('.tracking-camera-dock')
        ?.classList.remove('tracking-camera-dock-returning');
      if (fullscreenCleanupTimerRef.current) {
        window.clearTimeout(fullscreenCleanupTimerRef.current);
        fullscreenCleanupTimerRef.current = null;
      }
      return;
    }
    if (!wasFullscreenRef.current) return;
    wasFullscreenRef.current = false;
    const dockEl = tileEl.closest('.tracking-camera-dock');
    tileEl.classList.add('tracking-camera-tile-returning');
    dockEl?.classList.add('tracking-camera-dock-returning');
    window.requestAnimationFrame(() => {
      tileEl.getBoundingClientRect();
    });
    fullscreenCleanupTimerRef.current = window.setTimeout(() => {
      tileEl.classList.remove('tracking-camera-tile-returning');
      dockEl?.classList.remove('tracking-camera-dock-returning');
      fullscreenCleanupTimerRef.current = null;
    }, 450);
  }, []);

  useEffect(() => {
    handleFullscreenChange(isFullscreen);
  }, [handleFullscreenChange, isFullscreen]);

  useEffect(() => () => {
    if (fullscreenCleanupTimerRef.current) {
      window.clearTimeout(fullscreenCleanupTimerRef.current);
    }
  }, []);

  const handleScreenshot = useCallback(async event => {
    event.stopPropagation();
    const targetUrl = image?.url || image?.download_url;
    if (!targetUrl) return;

    const fileName =
      targetUrl.split('/').pop()?.split('?')[0] || `camera-ch${channel}.jpg`;

    try {
      const response = await fetch(targetUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      window.open(targetUrl, '_blank');
    }
  }, [channel, image?.download_url, image?.url]);

  const handleToggleFullscreen = useCallback(async event => {
    event.stopPropagation();
    const tileEl = containerRef.current;
    if (!tileEl) return;
    if (document.fullscreenElement === tileEl) {
      await document.exitFullscreen?.();
    } else {
      await tileEl.requestFullscreen?.();
    }
  }, []);

  return (
    <Box
      ref={containerRef}
      className="tracking-camera-tile tracking-camera-image-tile"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <img
        src={image.url}
        alt={`CH${channel}`}
        className="tracking-camera-image"
      />
      <Box className="tracking-camera-image-label">{`CH${channel}`}</Box>
      <TrackingCameraToolbar
        isVisible={isFullscreen ? isFullscreenHovered : isHovered}
        isMuted
        isFullscreen={isFullscreen}
        showMuteButton={false}
        actionIcon={<GetAppIcon />}
        actionLabel="Tải ảnh camera"
        actionTitle="Tải ảnh"
        onToggleMute={() => { }}
        onScreenshot={handleScreenshot}
        onToggleFullscreen={handleToggleFullscreen}
      />
    </Box>
  );
};

const TrackingCameraTile = ({ deviceSerial, channel }) => {
  const tileHandleRef = useRef(null);
  const containerRef = useRef(null);
  const qualityStorageKey = getLiveQualityStorageKey(deviceSerial, channel);
  const [primedQualityKey, setPrimedQualityKey] = useState('');
  const fullscreenCleanupTimerRef = useRef(null);
  const wasFullscreenRef = useRef(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFullscreenHovered, setIsFullscreenHovered] = useState(false);
  const fsTimeoutRef = useRef(null);
  const [mediaState, setMediaState] = useState({
    isLive: false,
    muted: true,
    volume: 0,
    // Three-state, never a boolean. 'unknown' is unproven, not negative.
    audioCapability: 'unknown',
    audioReceiving: false,
    audioUnavailableReason: null,
    isFullscreen: false,
    quality: 'sd',
    width: 0,
    height: 0,
  });
  const audio = useAudioAffordance(mediaState, `${deviceSerial}_${channel}`);

  useLayoutEffect(() => {
    primeDefaultLiveQuality(deviceSerial, channel, DEFAULT_LIVE_QUALITY);
    setPrimedQualityKey(qualityStorageKey);
  }, [channel, deviceSerial, qualityStorageKey]);

  useEffect(() => {
    const syncFullscreenState = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };

    document.addEventListener('fullscreenchange', syncFullscreenState);
    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreenState);
    };
  }, []);

  useEffect(() => {
    if (!isFullscreen) {
      setIsFullscreenHovered(false);
      if (fsTimeoutRef.current) {
        clearTimeout(fsTimeoutRef.current);
        fsTimeoutRef.current = null;
      }
      return;
    }

    const handleMouseMove = () => {
      setIsFullscreenHovered(true);
      if (fsTimeoutRef.current) {
        clearTimeout(fsTimeoutRef.current);
      }
      fsTimeoutRef.current = setTimeout(() => {
        setIsFullscreenHovered(false);
      }, 2500);
    };

    const handleMouseLeave = () => {
      setIsFullscreenHovered(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    handleMouseMove();

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      if (fsTimeoutRef.current) {
        clearTimeout(fsTimeoutRef.current);
      }
    };
  }, [isFullscreen]);

  const handleMediaStateChange = useCallback((next) => {
    setMediaState(next);
  }, []);

  const handleFullscreenChange = useCallback((isFs) => {
    const tileEl = containerRef.current;
    if (!tileEl) return;
    if (isFs) {
      wasFullscreenRef.current = true;
      tileEl.classList.remove('tracking-camera-tile-returning');
      tileEl.closest('.tracking-camera-dock')?.classList.remove('tracking-camera-dock-returning');
      if (fullscreenCleanupTimerRef.current) {
        window.clearTimeout(fullscreenCleanupTimerRef.current);
        fullscreenCleanupTimerRef.current = null;
      }
      return;
    }
    if (!wasFullscreenRef.current) return;
    wasFullscreenRef.current = false;
    const dockEl = tileEl.closest('.tracking-camera-dock');
    tileEl.classList.add('tracking-camera-tile-returning');
    dockEl?.classList.add('tracking-camera-dock-returning');
    window.requestAnimationFrame(() => { tileEl.getBoundingClientRect(); });
    fullscreenCleanupTimerRef.current = window.setTimeout(() => {
      tileEl.classList.remove('tracking-camera-tile-returning');
      dockEl?.classList.remove('tracking-camera-dock-returning');
      fullscreenCleanupTimerRef.current = null;
    }, 450);
  }, []);

  useEffect(() => {
    handleFullscreenChange(isFullscreen);
  }, [handleFullscreenChange, isFullscreen]);

  useEffect(() => () => {
    if (fullscreenCleanupTimerRef.current) {
      window.clearTimeout(fullscreenCleanupTimerRef.current);
    }
  }, []);

  // Synchronous by necessity: this is the gesture that lets the browser allow
  // AudioContext.resume(). Awaiting anything first loses it.
  const handleToggleMute = useCallback((event) => {
    event.stopPropagation();
    if (audio.disabled) return;
    tileHandleRef.current?.setMuted(!mediaState.muted);
  }, [mediaState.muted, audio.disabled]);

  const handleScreenshot = useCallback(async (event) => {
    event.stopPropagation();
    await tileHandleRef.current?.downloadScreenshot();
  }, []);

  const handleToggleFullscreen = useCallback(async (event) => {
    event.stopPropagation();
    const tileEl = containerRef.current;
    if (!tileEl) return;
    if (document.fullscreenElement === tileEl) {
      await document.exitFullscreen?.();
    } else {
      await tileEl.requestFullscreen?.();
    }
  }, []);

  const toolbar = (
    <TrackingCameraToolbar
      isVisible={mediaState.isLive && (isFullscreen ? isFullscreenHovered : isHovered)}
      isMuted={mediaState.muted}
      audio={audio}
      isFullscreen={isFullscreen}
      onToggleMute={handleToggleMute}
      onScreenshot={handleScreenshot}
      onToggleFullscreen={handleToggleFullscreen}
    />
  );

  return (
    <Box
      ref={containerRef}
      className={`tracking-camera-tile ${mediaState.isLive ? 'tracking-camera-tile-live' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {primedQualityKey === qualityStorageKey && (
        <CameraTile
          ref={tileHandleRef}
          deviceSerial={deviceSerial}
          channelId={channel}
          title={`CH${channel}`}
          height="100%"
          initialQuality={DEFAULT_LIVE_QUALITY}
          onMediaStateChange={handleMediaStateChange}
          onFullscreenChange={handleFullscreenChange}
          recoveringOverlayDelayMs={RECOVERING_OVERLAY_DELAY_MS}
          pauseWhenHidden
        />
      )}
      {toolbar}
    </Box>
  );
};

const TrackingCameraPanel = ({
  deviceSerial,
  viewCount,
  deviceType,
  rawImages = [],
  isAllowCamera = 1,
  isLiveMode = false
}) => {
  const maxCameraCount = getMaxCameraCount(deviceType);
  const visibleChannels = CAMERA_CHANNELS
    .slice(0, Math.min(viewCount, maxCameraCount))
    .reverse();
  const snapshotByChannel = useMemo(() => {
    return rawImages.reduce((acc, image) => {
      const channel = getBaseChannelFromImageCamera(image?.camera);
      if (!channel || acc[channel]) {
        return acc;
      }

      acc[channel] = image;
      return acc;
    }, {});
  }, [rawImages]);
  const hasSnapshotImages = Number(isAllowCamera) !== 0 && rawImages.length > 0;
  const shouldRenderLiveStream = Number(isAllowCamera) === 2 && isLiveMode;

  return (
    <Box className="tracking-camera-panel tracking-camera-panel-vertical">
      {visibleChannels.map(channel => (
        <Box className="tracking-camera-slot" key={channel}>
          {shouldRenderLiveStream && deviceSerial && LIVE_CAMERA_CHANNELS.has(channel) ? (
            <TrackingCameraTile deviceSerial={deviceSerial} channel={channel} />
          ) : hasSnapshotImages && snapshotByChannel[channel] ? (
            <TrackingCameraImageTile image={snapshotByChannel[channel]} channel={channel} />
          ) : (
            // Both remaining cases are a placeholder. Removing the auto-live
            // fallback left `hasSnapshotImages ? <CameraPlaceholder/> :
            // <CameraPlaceholder/>`, which reads as if the two branches differ.
            <CameraPlaceholder />
          )}
        </Box>
      ))}
    </Box>
  );
};

const TrackingCameraControls = ({
  viewCount,
  deviceType,
  deviceStatus = '',
  onChangeViewCount,
  isAllowCamera = 1,
  isLiveMode = false,
  onToggleLiveMode = () => { }
}) => {
  const dispatch = useDispatch();
  const [isViewMenuOpen, setIsViewMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const closeMenuTimerRef = useRef(null);
  const viewOptions = getCameraViewOptions(deviceType);
  const reversedViewOptions = [...viewOptions].reverse();
  const canToggleLiveMode = Number(isAllowCamera) === 2;
  const normalizedDeviceStatus = String(deviceStatus || '').toLowerCase();

  const openViewMenu = useCallback(() => {
    if (closeMenuTimerRef.current) {
      window.clearTimeout(closeMenuTimerRef.current);
      closeMenuTimerRef.current = null;
    }
    setIsViewMenuOpen(true);
  }, []);

  const closeViewMenu = useCallback(() => {
    if (closeMenuTimerRef.current) {
      window.clearTimeout(closeMenuTimerRef.current);
    }
    closeMenuTimerRef.current = window.setTimeout(() => {
      setIsViewMenuOpen(false);
      closeMenuTimerRef.current = null;
    }, 180);
  }, []);

  useEffect(() => {
    if (!isViewMenuOpen) return undefined;

    const handleClickOutside = event => {
      if (!menuRef.current?.contains(event.target)) {
        setIsViewMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isViewMenuOpen]);

  useEffect(() => () => {
    if (closeMenuTimerRef.current) {
      window.clearTimeout(closeMenuTimerRef.current);
    }
  }, []);

  const handleToggleLiveCamera = useCallback(() => {
    if (
      // normalizedDeviceStatus === 'stopped' ||
      normalizedDeviceStatus === LOST_GSM ||
      !canToggleLiveMode
    ) {
      dispatch(
        showGlobalToast({
          type: 'warning',
          message:
            'Thiết bị hoặc cấu hình hiện tại không hỗ trợ xem Live!'
        })
      );
      return;
    }

    onToggleLiveMode();
  }, [
    canToggleLiveMode,
    dispatch,
    normalizedDeviceStatus,
    onToggleLiveMode
  ]);

  return (
    <Box
      className="tracking-camera-view-control"
      ref={menuRef}
    >
      <Box
        className="tracking-camera-grid-anchor"
        onMouseEnter={openViewMenu}
        onMouseLeave={closeViewMenu}
      >
        <Box
          component="button"
          type="button"
          className="tracking-camera-grid-button"
          aria-haspopup="menu"
          aria-expanded={isViewMenuOpen}
          aria-label="Chọn bố cục camera"
          title="Lưới"
          onFocus={openViewMenu}
        >
          <AppsIcon style={{ fontSize: 18 }} />
        </Box>
        {isViewMenuOpen && (
          <Box className="tracking-camera-view-menu">
            {reversedViewOptions.map(option => (
              <Box
                key={option.label}
                component="button"
                type="button"
                className={`tracking-camera-view-option ${viewCount === option.count ? 'active' : ''
                  }`}
                onClick={() => {
                  onChangeViewCount(option.count);
                  setIsViewMenuOpen(false);
                }}
                aria-pressed={viewCount === option.count}
                aria-label={`Hiển thị ${option.count} camera`}
                title={`Hiển thị ${option.count} camera`}
              >
                <CameraViewOptionIcon count={option.count} />
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Box
        component="button"
        type="button"
        className="tracking-camera-icon-box"
        onClick={handleToggleLiveCamera}
        aria-pressed={isLiveMode}
        aria-label="Phát livestream camera"
        title={
          canToggleLiveMode
            ? 'Phát livestream camera'
            : 'Camera không hỗ trợ livestream'
        }
        style={{
          color: canToggleLiveMode && isLiveMode ? '#c00000' : undefined,
          background: canToggleLiveMode && isLiveMode ? '#fdf0f2' : undefined,
        }}
      >
        <VideocamIcon style={{ fontSize: 18 }} />
      </Box>
    </Box>
  );
};

const TrackingCameraDock = ({
  deviceSerial,
  deviceType,
  deviceStatus = '',
  viewCount,
  showMenu,
  isMapFullscreen = false,
  leftOffset = 0,
  onChangeViewCount,
  rawImages = [],
  isAllowCamera = 1
}) => {
  const dispatch = useDispatch();
  const [isLiveMode, setIsLiveMode] = useState(false);
  const previousDeviceSerialRef = useRef(deviceSerial);
  const normalizedIsAllowCamera = Number(isAllowCamera) || 1;

  useEffect(() => {
    if (previousDeviceSerialRef.current === deviceSerial) {
      return;
    }

    previousDeviceSerialRef.current = deviceSerial;
    setIsLiveMode(false);
  }, [deviceSerial]);

  useEffect(() => {
    if (normalizedIsAllowCamera !== 2 && isLiveMode) {
      setIsLiveMode(false);
    }
  }, [isLiveMode, normalizedIsAllowCamera]);

  useEffect(() => {
    const refreshForWasmMemory = () => {
      dispatch(
        showGlobalToast({
          type: 'info',
          message: 'Đang làm mới trình phát video để duy trì ổn định.'
        })
      );
    };
    window.addEventListener(WASM_MEMORY_REFRESH_EVENT, refreshForWasmMemory);
    return () => window.removeEventListener(WASM_MEMORY_REFRESH_EVENT, refreshForWasmMemory);
  }, [dispatch]);

  return (
    <Box
      className={`tracking-camera-dock tracking-camera-dock-vertical ${showMenu ? 'tracking-camera-dock-menu-open' : ''
        } ${isMapFullscreen && viewCount === 4 ? 'tracking-camera-dock-fill-height' : ''}`}
      style={{
        '--camera-count': Math.min(viewCount, getMaxCameraCount(deviceType)),
        left: `${leftOffset}px`
      }}
    >
      <TrackingCameraPanel
        deviceSerial={deviceSerial}
        deviceType={deviceType}
        viewCount={viewCount}
        rawImages={rawImages}
        isAllowCamera={normalizedIsAllowCamera}
        isLiveMode={isLiveMode}
      />
      <TrackingCameraControls
        deviceType={deviceType}
        deviceStatus={deviceStatus}
        viewCount={viewCount}
        onChangeViewCount={onChangeViewCount}
        isAllowCamera={normalizedIsAllowCamera}
        isLiveMode={isLiveMode}
        onToggleLiveMode={() => {
          if (normalizedIsAllowCamera !== 2) {
            return;
          }
          setIsLiveMode(prev => !prev);
        }}
      />
    </Box>
  );
};

export default TrackingCameraDock;
